import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import cn from 'classnames'
import { Button, TextInput } from '@forwardflow/ui-v2'
import {
   BoltIcon,
   BullsEyeArrowIcon,
   CalendarCheckIcon,
   CheckCircle02Icon,
   ChevronRightIcon,
   ClockIcon,
   Message01Icon,
   PondIcon,
   ZapIcon,
} from '@forwardflow/icons'
import useSetupProgress from '@forwardflow/hooks/customHooks/onboarding/useSetupProgress'
import { useA2PRegistrationModalStore } from '@forwardflow/store/modal'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { formatNumber } from '@forwardflow/helpers/utils/formatNumber.ts'
import { composerPath, isSandbox, wizardPath } from '../dev/devTools'
import { LinkButton, Skeleton } from '../shell/Card'
import { IStarterCampaign } from '../steps/first-campaign/starter-campaigns'
import { useLeadCount } from '../steps/first-campaign/useLeadCount'
import { useCampaignVocabulary } from '../composer/useCampaignVocabulary'
import { useLaunchedCampaigns } from '../composer/launched-store'
import { useCyclingPlaceholder } from '../composer/useCyclingPlaceholder'

const HIDE_CONNECT_KEY = 'ff-home:hide-connect'

const StarterIcon = ({ template }: { template: IStarterCampaign['template'] }) => {
   const cls = 'w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-[#9F7BFF]/15 text-[#A684FF]'
   if (template === 'revive')
      return (
         <span className={cls}>
            <BullsEyeArrowIcon width={18} height={18} />
         </span>
      )
   if (template === 'pond')
      return (
         <span className={cls}>
            <PondIcon width={18} height={18} />
         </span>
      )
   return (
      <span className={cls}>
         <ZapIcon width={18} height={18} />
      </span>
   )
}

const StarterCard = ({ campaign, onPick }: { campaign: IStarterCampaign; onPick: (c: IStarterCampaign, count?: number) => void }) => {
   const { count, loading } = useLeadCount(campaign.criteria, campaign.knownCount)
   return (
      <button
         type="button"
         onClick={() => onPick(campaign, count)}
         data-testid={`starter-${campaign.template}`}
         className="text-left rounded-xl border border-white/10 bg-white/[0.03] hover:border-[#9F7BFF]/60 hover:bg-[#9F7BFF]/[0.06] p-4 transition-all flex flex-col gap-2.5 w-full"
      >
         <div className="flex items-center gap-3">
            <StarterIcon template={campaign.template} />
            <div className="min-w-0">
               <p className="text-white font-semibold text-sm truncate">{campaign.title}</p>
               {campaign.recommended && (
                  <span className="text-[10px] uppercase tracking-wide font-semibold text-[#C9B8FF]">Best place to start</span>
               )}
            </div>
         </div>
         <p className="text-[#99A1AF] text-xs leading-5">{campaign.description}</p>
         <p className="text-white/80 text-xs mt-auto">
            {loading && count === undefined ? (
               <span className="inline-block h-3.5 w-20 rounded bg-white/10 animate-pulse align-middle" />
            ) : count !== undefined ? (
               <>
                  <b className="text-white">{formatNumber(count)}</b> {count === 1 ? 'lead' : 'leads'} match today
               </>
            ) : (
               'Counting your leads'
            )}
         </p>
      </button>
   )
}

/**
 * The dashboard before the first campaign: one job, start calling. A text
 * box that takes the customer's own words, three campaigns built from the
 * account, and underneath, quietly, texting approval, the calendar and
 * the assistant connection.
 */
export const LaunchHome = ({
   isCalendarConnected,
   onConnectCalendar,
}: {
   isCalendarConnected?: boolean
   onConnectCalendar?: () => void
}) => {
   const navigate = useNavigate()
   const { starters, loading } = useCampaignVocabulary()
   const { data: progress } = useSetupProgress()
   const { onboardingData, goToStep } = useOnboardingStoreV2()
   const { openA2PRegistrationModal } = useA2PRegistrationModalStore()
   const launched = useLaunchedCampaigns((s) => s.campaigns)
   const [text, setText] = useState('')
   const placeholder = useCyclingPlaceholder(!text)
   const [hideConnect, setHideConnect] = useState(() => {
      try {
         return localStorage.getItem(HIDE_CONNECT_KEY) === '1'
      } catch {
         return false
      }
   })

   const go = (state: { text?: string; starter?: IStarterCampaign }) => navigate(composerPath(), { state })

   /* ---------- Texting approval ---------- */

   const a2p = progress?.onboardingProgress?.a2pRegistration?.status
   const smsOn = progress?.deploymentConfig?.channelConfig?.smsEnabled
   const filedHere = Boolean(onboardingData.businessDetails?.businessRegistrationNumber)
   const textingState: 'done' | 'waiting' | 'next' =
      smsOn || a2p === 'completed' ? 'done' : a2p === 'in_progress' || filedHere ? 'waiting' : 'next'
   const calendarDone = Boolean(isCalendarConnected) || onboardingData.isCalendarConnected
   const confirmBusiness = () => {
      if (isSandbox()) {
         goToStep('business-details')
         navigate(wizardPath())
      } else openA2PRegistrationModal()
   }
   const hideConnectCard = () => {
      try {
         localStorage.setItem(HIDE_CONNECT_KEY, '1')
      } catch {
         // ignore
      }
      setHideConnect(true)
   }

   const live = launched[0]

   return (
      <div className="space-y-4" data-testid="launch-home">
         {live && (
            <div className="rounded-2xl border border-[#9F7BFF]/40 bg-[#9F7BFF]/10 p-5 md:p-6" data-testid="live-campaign">
               <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <span className="flex shrink-0 items-center justify-center w-11 h-11 rounded-xl bg-[#9F7BFF]/20 text-[#A684FF]">
                     <BoltIcon width={22} height={22} />
                  </span>
                  <div className="flex-1 min-w-0">
                     <p className="text-white font-semibold text-lg leading-tight">
                        {live.name} is live
                     </p>
                     <p className="text-[#99A1AF] text-sm mt-1">
                        {live.assistantName} is calling{live.leads !== undefined ? ` ${formatNumber(live.leads)}` : ''} leads
                        {live.areaCodes.length ? ` from ${live.areaCodes.map((c) => `(${c})`).join(', ')} numbers` : ''}. Interested
                        leads are transferred to you, the rest get an appointment booked. Stats show up here once the first calls go out.
                     </p>
                  </div>
                  <Button size="middle" variant="secondary" className="!h-10" onClick={() => go({})}>
                     Start another campaign
                  </Button>
               </div>
            </div>
         )}

         <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 md:p-7">
            <p className="text-white/45 text-xs uppercase tracking-wide mb-1">
               {live ? 'Next campaign' : 'Your first campaign'}
            </p>
            <h1 className="text-white font-semibold text-2xl leading-tight">Start calling your leads</h1>
            <p className="text-[#99A1AF] text-sm mt-2 max-w-[640px]">
               Tell us who to call in your own words, or pick one of the campaigns we built from your Follow Up Boss
               account. We call, qualify, and hand the interested ones to you.
            </p>

            <div className="mt-5 flex flex-col sm:flex-row gap-2">
               <TextInput
                  className="flex-1"
                  value={text}
                  placeholder={placeholder}
                  onChange={(e) => setText(e.currentTarget.value)}
                  onKeyDown={(e) => {
                     if (e.key === 'Enter' && text.trim()) go({ text: text.trim() })
                  }}
                  data-testid="home-describe"
               />
               <Button size="middle" className="!h-11 sm:!px-5" onClick={() => go({ text: text.trim() })} disabled={!text.trim()}>
                  Set up this campaign <ChevronRightIcon width={16} />
               </Button>
            </div>

            <p className="text-white/45 text-xs mt-5 mb-2.5">Or start with one of these, built from your account</p>
            {loading && !starters.length ? (
               <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Skeleton className="h-32" />
                  <Skeleton className="h-32" />
                  <Skeleton className="h-32" />
               </div>
            ) : starters.length ? (
               <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="starter-cards">
                  {starters.map((c) => (
                     <StarterCard key={c.id} campaign={c} onPick={(campaign, count) => go({ starter: { ...campaign, knownCount: count ?? campaign.knownCount } })} />
                  ))}
               </div>
            ) : (
               <p className="text-white/50 text-sm">
                  We're still pulling your lead sources in from Follow Up Boss. Describe who to call above, or check back
                  in a few minutes.
               </p>
            )}
         </div>

         <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div
               className="rounded-xl border border-white/10 bg-white/[0.03] p-4 flex items-start gap-3"
               data-testid="texting-card"
               data-state={textingState}
            >
               {textingState === 'done' ? (
                  <CheckCircle02Icon width={20} height={20} color="#00D492" className="mt-0.5 shrink-0" />
               ) : textingState === 'waiting' ? (
                  <ClockIcon width={20} height={20} color="#A684FF" className="mt-0.5 shrink-0" />
               ) : (
                  <Message01Icon width={20} height={20} color="#A684FF" className="mt-0.5 shrink-0" />
               )}
               <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium">
                     {textingState === 'done' ? 'Texting is on' : textingState === 'waiting' ? 'Texting: with the carriers' : 'Texting: not switched on yet'}
                  </p>
                  <p className="text-white/50 text-xs mt-0.5">
                     {textingState === 'done'
                        ? 'Your campaigns text as well as call.'
                        : textingState === 'waiting'
                          ? 'Your business details are being verified, usually 3 to 5 days. Calls and voicemails run meanwhile; texts join by themselves.'
                          : 'Carriers need your business details first. Two minutes. Calls and voicemails work right now.'}
                  </p>
                  {textingState === 'next' && (
                     <LinkButton className="mt-2 !text-xs" onClick={confirmBusiness} testId="confirm-business">
                        Confirm business details →
                     </LinkButton>
                  )}
               </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 flex items-start gap-3" data-testid="calendar-card">
               {calendarDone ? (
                  <CheckCircle02Icon width={20} height={20} color="#00D492" className="mt-0.5 shrink-0" />
               ) : (
                  <CalendarCheckIcon width={20} height={20} color="#A684FF" className="mt-0.5 shrink-0" />
               )}
               <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium">
                     {calendarDone ? 'Appointments land on your calendar' : 'Appointments: into Follow Up Boss for now'}
                  </p>
                  <p className="text-white/50 text-xs mt-0.5">
                     {calendarDone
                        ? 'Every booking shows up with the lead’s details.'
                        : 'Connect Google or Outlook once and we book straight into it.'}
                  </p>
                  {!calendarDone && (
                     <LinkButton
                        className="mt-2 !text-xs"
                        onClick={() => {
                           if (isSandbox()) {
                              goToStep('your-number')
                              navigate(wizardPath())
                           } else onConnectCalendar?.()
                        }}
                     >
                        Connect calendar →
                     </LinkButton>
                  )}
               </div>
            </div>

            {!hideConnect && (
               <div
                  className={cn('relative rounded-xl border border-white/10 bg-white/[0.02] p-4 flex items-start gap-3')}
                  data-testid="connect-card"
               >
                  <button
                     type="button"
                     aria-label="Hide"
                     className="absolute top-2 right-2.5 text-white/30 hover:text-white text-sm leading-none"
                     onClick={hideConnectCard}
                  >
                     ×
                  </button>
                  <BoltIcon width={20} height={20} color="#A684FF" className="mt-0.5 shrink-0 opacity-70" />
                  <div className="flex-1 min-w-0">
                     <p className="text-white/80 text-sm font-medium">Use Forward Flow from ChatGPT or Claude</p>
                     <p className="text-white/45 text-xs mt-0.5">
                        Ask about your leads and campaigns from the assistant you already use. Optional.
                     </p>
                     <LinkButton
                        className="mt-2 !text-xs !text-white/60"
                        onClick={() => (isSandbox() ? undefined : navigate('/connect/assistant'))}
                        disabled={isSandbox()}
                     >
                        {isSandbox() ? 'Available after sign-in' : 'Connect →'}
                     </LinkButton>
                  </div>
               </div>
            )}
         </div>
      </div>
   )
}
